import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth, API } from "../auth";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Search, Filter, FileDown, Plus, Image as ImageIcon, X, Clock } from "lucide-react";
import { parseLocalDate } from "../utils/date";

export default function BandeosList() {
  const { authHeaders, user } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ chain: "", user_id: "", date_from: "", date_to: "", search: "" });
  const [showFilters, setShowFilters] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const [list, st] = await Promise.all([
        axios.get(`${API}/bandeos`, { headers: authHeaders(), params }),
        axios.get(`${API}/bandeos/stats`, { headers: authHeaders() }),
      ]);
      setItems(list.data);
      setStats(st.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => {
    load();
    if (user?.role === "admin") {
      axios.get(`${API}/users`, { headers: authHeaders() }).then((r) => setUsers(r.data)).catch(() => {});
    }
  }, []);

  const clearFilters = () => {
    setFilters({ chain: "", user_id: "", date_from: "", date_to: "", search: "" });
    setTimeout(load, 0);
  };

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const url = `${API}/bandeos/export/excel?${params.toString()}`;
      const resp = await axios.get(url, { headers: authHeaders(), responseType: "blob" });
      const blob = new Blob([resp.data], { type: resp.headers["content-type"] });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `bandeo_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Excel descargado");
    } catch (e) {
      toast.error("Error al exportar");
    }
  };

  return (
    <div className="p-5 md:p-8 lg:p-12 max-w-[1400px] mx-auto" data-testid="bandeos-list-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="kbd-label mb-2 text-emerald-700">/ Bandeo</div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950">Bandeos</h1>
          <p className="text-slate-500 mt-2">Registros de atados en puntos de venta.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="border-slate-200 gap-2" data-testid="toggle-filters-btn">
            <Filter className="w-4 h-4" /> Filtros
          </Button>
          <Button onClick={exportExcel} variant="outline" className="border-slate-200 gap-2" data-testid="export-excel-btn">
            <FileDown className="w-4 h-4" /> Exportar Excel
          </Button>
          <Link to="/bandeos/new" className="inline-flex items-center gap-2 bg-emerald-700 text-white px-4 py-2 rounded-md hover:bg-emerald-800 font-medium" data-testid="new-bandeo-btn">
            <Plus className="w-4 h-4" /> Nuevo
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-6">
        {[
          { label: "Hoy", v: stats?.today ?? 0 },
          { label: "Esta semana", v: stats?.week ?? 0 },
          { label: "Este mes", v: stats?.month ?? 0 },
          { label: "Total", v: stats?.total ?? 0 },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-slate-200 p-4 md:p-5 rounded-lg">
            <span className="kbd-label">{s.label}</span>
            <div className="tv-stat-value text-3xl md:text-4xl text-slate-950 mt-1">{s.v}</div>
          </div>
        ))}
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Buscar por cadena, punto de venta, producto…"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          onKeyDown={(e) => e.key === "Enter" && load()}
          className="pl-10 h-12 border-slate-300 rounded-md"
          data-testid="search-input"
        />
      </div>

      {showFilters && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 mb-6 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="filters-panel">
          <div>
            <Label className="kbd-label">Cadena</Label>
            <Input value={filters.chain} onChange={(e) => setFilters({ ...filters, chain: e.target.value })} className="mt-1.5" data-testid="filter-chain" />
          </div>
          {user?.role === "admin" && (
            <div>
              <Label className="kbd-label">Usuario</Label>
              <Select value={filters.user_id || "__all"} onValueChange={(v) => setFilters({ ...filters, user_id: v === "__all" ? "" : v })}>
                <SelectTrigger className="mt-1.5" data-testid="filter-user"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all">Todos</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="kbd-label">Desde</Label>
            <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="mt-1.5" data-testid="filter-date-from" />
          </div>
          <div>
            <Label className="kbd-label">Hasta</Label>
            <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="mt-1.5" data-testid="filter-date-to" />
          </div>
          <div className="col-span-full flex gap-2">
            <Button onClick={load} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="apply-filters-btn">Aplicar filtros</Button>
            <Button variant="outline" onClick={clearFilters} className="gap-2" data-testid="clear-filters-btn"><X className="w-4 h-4" /> Limpiar</Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-lg p-16 text-center text-slate-400 kbd-label">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-16 text-center">
          <p className="font-display text-2xl text-slate-900 mb-2">Sin bandeos aún</p>
          <p className="text-sm text-slate-500">Crea el primer registro de bandeo.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((b) => {
            const totalPhotos = (b.delivery_doc_photos?.length || 0) + (b.tying_photos?.length || 0);
            return (
              <Link to={`/bandeos/${b.id}`} key={b.id} className="group bg-white border border-slate-200 rounded-lg p-5 hover:border-emerald-600 hover:shadow-sm transition-all" data-testid={`bandeo-card-${b.id}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-700 text-white font-display font-bold flex flex-col items-center justify-center rounded-md">
                      <span className="text-[9px] leading-none opacity-70">{parseLocalDate(b.fecha).toLocaleDateString("es", { month: "short" }).toUpperCase()}</span>
                      <span className="text-lg leading-tight">{parseLocalDate(b.fecha).getDate()}</span>
                    </div>
                    <div>
                      <div className="font-display font-bold text-lg text-slate-950 leading-tight">{b.chain}</div>
                      <div className="kbd-label !text-[10px]">{b.branch}</div>
                    </div>
                  </div>
                  {totalPhotos > 0 && (
                    <div className="flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded"><ImageIcon className="w-3 h-3" /> {totalPhotos}</div>
                  )}
                </div>
                <div className="space-y-1 text-sm">
                  {b.arrival_time && (<div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-slate-400" /><span className="text-slate-700">Llegada {b.arrival_time}{b.departure_time && ` · Salida ${b.departure_time}`}</span></div>)}
                  {b.delivery_qty && (<div><span className="kbd-label">Entrega</span><span className="ml-2 text-slate-900">{b.delivery_qty}</span></div>)}
                  {b.product_banded && (<div><span className="kbd-label">Atado</span><span className="ml-2 text-slate-900">{b.product_banded}{b.banded_to_product && ` → ${b.banded_to_product}`}</span></div>)}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
